import { useCallback, useEffect, useId, useState } from 'react'
import { useLang } from '../i18n'
import { claudeTokenBridge, type ClaudeTokenStatus } from '../lib/desktop'

/** Warn from a month out — enough notice to mint a replacement without any urgency. */
const EXPIRY_WARN_MS = 30 * 24 * 60 * 60 * 1000

/**
 * Settings → AI agent → Claude → the opt-in **long-lived token** panel (`claude setup-token`).
 *
 * A member who opts in stops re-signing in every few weeks: the token lasts a year, Niobe keeps it
 * encrypted on this machine and passes it only to the Claude processes it starts.
 *
 * ## Why this renders from the BRIDGE and not from /agent/status
 *
 * Both know about the token, and they would disagree at exactly the wrong moments: the passerelle's
 * `tokenRejected` is in-memory, so it is false again after a relaunch, while the shell's
 * `claude-token.json` still says rejected — and the passerelle answers on a 10 s cache, so a Save
 * would appear not to have happened. One source on screen, and it is the one that owns the file.
 * `/agent/status.tokenMode` still exists, and is what suppresses #54's expiry warning elsewhere.
 *
 * ## Why it is additive, never a replacement
 *
 * The Claude card's existing branch chain is untouched. When `authenticated` is false the card
 * still shows `ClaudeLoginCta` above this, and signing in there really does restore the chat — that
 * remains true even in the rejected state, because a rejected token is no longer injected and the
 * `/login` credential is what authenticates again.
 *
 * ## Absent by default
 *
 * `claudeTokenBridge()` is null in a plain browser, with the desktop flag off (the default — see
 * client/desktop/config.js for the compliance caveats), and in dev-live. This renders nothing at
 * all in each of those, rather than explaining an affordance that is not there.
 */
export function ClaudeTokenSettings() {
  const { t, lang } = useLang()
  const bridge = claudeTokenBridge()
  const [status, setStatus] = useState<ClaudeTokenStatus | null>(null)
  const [draft, setDraft] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  /** The `claude setup-token` command, once the shell has said it cannot open a terminal itself. */
  const [manualCommand, setManualCommand] = useState<string | null>(null)
  const inputId = useId()

  const refresh = useCallback(async (): Promise<void> => {
    if (!bridge) return
    setStatus(await bridge.status())
  }, [bridge])

  useEffect(() => {
    void refresh()
  }, [refresh])

  if (!bridge) return null

  const formatDate = (ms: number): string =>
    new Date(ms).toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-US', { year: 'numeric', month: 'long', day: 'numeric' })

  async function generate(): Promise<void> {
    if (!bridge) return
    setError(null)
    const res = await bridge.openSetupTerminal()
    // Not Windows (or the shell refused): show the command so the member can run it themselves.
    if (!res.supported || res.error) setManualCommand(res.command ?? 'claude setup-token')
    if (res.error) setError(res.error)
  }

  async function save(): Promise<void> {
    if (!bridge) return
    setError(null)
    setBusy(true)
    try {
      // The shell validates the shape, then spends ONE real Claude request proving the token works
      // before anything is written — a well-formed but invalid paste is refused here, not weeks
      // later in the middle of somebody's chat.
      const res = await bridge.set(draft.trim())
      if (!res.ok) {
        setError(
          res.reason === 'malformed'
            ? t('claude_token_error_malformed')
            : res.reason === 'unauthorized' || res.reason === 'unavailable'
              ? t('claude_token_error_unavailable')
              : res.kind === 'transient'
                ? t('claude_token_error_transient')
                : res.reason ?? t('claude_token_error_refused'),
        )
        return
      }
      setDraft('') // never keep the secret around once it is stored
      await refresh()
    } finally {
      setBusy(false)
    }
  }

  async function remove(): Promise<void> {
    if (!bridge) return
    setError(null)
    setBusy(true)
    try {
      await bridge.remove()
      setManualCommand(null)
      await refresh()
    } finally {
      setBusy(false)
    }
  }

  const present = status?.present === true
  const rejected = present && status?.rejected === true
  const active = present && !rejected
  const expiresAt = status?.expiresAt
  const expiringSoon = active && typeof expiresAt === 'number' && expiresAt - Date.now() < EXPIRY_WARN_MS

  return (
    <div className="claude-token">
      <p className="claude-token-title">
        <b>{t('claude_token_title')}</b>
      </p>

      {active && (
        <>
          <p className="claude-token-active">
            {typeof expiresAt === 'number'
              ? t('claude_token_active').replace('{date}', () => formatDate(expiresAt))
              : t('claude_token_active_nodate')}
          </p>
          {expiringSoon && <p className="claude-token-warn">{t('claude_token_expiring')}</p>}
          {status?.persisted === false && <p className="claude-token-warn">{t('claude_token_not_persisted')}</p>}
          {/* The one thing a member WILL get wrong otherwise: signing in again looks like the fix
              for anything Claude-shaped, and while this token is active it changes nothing. */}
          <p className="claude-token-note">{t('claude_token_outranks_login')}</p>
          <div className="claude-token-actions">
            <button type="button" className="mini ghost" disabled={busy} onClick={() => void remove()}>
              {t('claude_token_remove')}
            </button>
          </div>
        </>
      )}

      {rejected && (
        <>
          <p className="claude-token-rejected">
            {status?.rejectedReason
              ? t('claude_token_rejected_reason').replace('{reason}', () => status.rejectedReason as string)
              : t('claude_token_rejected')}
          </p>
          {/* Exactly what is true now. Niobe is back on the member's usual sign-in, which may well
              be perfectly valid — so this must not read as "everything Claude is broken". */}
          <p className="claude-token-note">{t('claude_token_rejected_fallback')}</p>
          <div className="claude-token-actions">
            <button type="button" className="mini ghost" disabled={busy} onClick={() => void remove()}>
              {t('claude_token_remove')}
            </button>
            <button type="button" className="mini ok" onClick={() => void generate()}>
              {t('claude_token_generate_new')}
            </button>
          </div>
        </>
      )}

      {!present && (
        <>
          <p className="claude-token-note">{t('claude_token_explain')}</p>
          <div className="claude-token-actions">
            <button type="button" className="mini ok" onClick={() => void generate()}>
              {t('claude_token_generate')}
            </button>
          </div>
        </>
      )}

      {/* Shown after Generate on a platform where Niobe cannot open a console (macOS/Linux — see
          DEBT.md), and after a rejection when the member asks for a new one. */}
      {manualCommand && (
        <>
          <p className="claude-token-note">{t('claude_token_manual')}</p>
          <code className="claude-token-cmd mono">{manualCommand}</code>
          <div className="claude-token-actions">
            <button
              type="button"
              className="mini ghost"
              onClick={() => void navigator.clipboard?.writeText(manualCommand)}
            >
              {t('claude_token_copy')}
            </button>
          </div>
        </>
      )}

      {/* The paste field stays available in the rejected state too: Generate a new one → Save is one
          continuous move, and hiding it would make the member remove first for no reason. */}
      {!active && (
        <>
          <label className="claude-token-label" htmlFor={inputId}>
            {t('claude_token_paste')}
          </label>
          <div className="claude-token-actions">
            <input
              id={inputId}
              className="claude-token-input"
              type="password"
              autoComplete="off"
              spellCheck={false}
              value={draft}
              disabled={busy}
              aria-invalid={error ? 'true' : undefined}
              onChange={(e) => setDraft(e.target.value)}
            />
            <button type="button" className="mini ok" disabled={busy || draft.trim() === ''} onClick={() => void save()}>
              {t('claude_token_save')}
            </button>
          </div>
          {busy && (
            <p className="claude-token-note" role="status" aria-live="polite">
              {t('claude_token_saving')}
            </p>
          )}
          <p className="claude-token-note">{t('claude_token_caveats')}</p>
        </>
      )}

      {error && (
        <p className="claude-token-error" role="status" aria-live="polite">
          {error}
        </p>
      )}
    </div>
  )
}
